<script lang="ts">
import { Component, Mixins, Watch, Prop } from "vue-property-decorator";
import Chart from "chart.js";
import { Doughnut } from "vue-chartjs";

/**
 * Chartjs Doughuntコンポーネント
 *
 * #Topic:
 * Vue以外をextendsする場合、vue-property-decoratorのMixinsを使用してextendsしないとエラーになるので注意
 * ※Uncaught TypeError: Super expression must either be null or a function が出力される
 */
@Component
export default class DoughnutChart extends Mixins(Doughnut) {
  /** グラフに表示する内容 */
  @Prop({
    default: () => {
      return {};
    }
  })
  chartData!: Chart.ChartData;

  /** プラグイン */
  @Prop({
    default: () => {
      return [
        {
          id: "centerLabel",
          beforeDatasetDraw(chart: any, args: any, options: Chart.ChartOptions) {
            DoughnutChart.centerLabelPlugin(chart, options);
          }
        }
      ];
    }
  })
  plugins!: any;

  @Prop({ default: "回" })
  labelSuffix!: string;

  /**
   * propの値が更新されたらグラフを再描画する
   * chartjsのグラフ再描画を使用すると、高さがリセットされるようなので自前で行う
   */
  @Watch("chartData")
  public chartDataChanged(): void {
    const _this: DoughnutChart = this;
    this.renderChart(this.chartData, {
      responsive: true,
      maintainAspectRatio: false,
      // グラフの内側を何%切り取るか
      cutoutPercentage: 70,
      // 凡例
      legend: {
        position: "left",
        labels: {
          boxWidth: 14,
          fontSize: 14,
          generateLabels: function (chart: any) {
            return _this.createLegends(chart);
          }
        }
      },
      // ツールチップ
      tooltips: {
        backgroundColor: "rgba(255, 255, 255, 1)",
        borderColor: "rgba(0, 0, 0, .2)",
        borderWidth: 1,
        bodyFontColor: "rgba(0, 0, 0, 1)",
        bodyFontSize: 14,
        xPadding: 8,
        yPadding: 12,
        callbacks: {
          label: function (tooltipItem: Chart.ChartTooltipItem, data: any) {
            return _this.createTooltipLabel(tooltipItem, data);
          }
        }
      }
    });

    return;
  }

  /**
   * 凡例ラベルを作成する
   */
  public createLegends(chart: any) {
    const chartData = chart.tooltip._data;
    const chartDataset = chartData.datasets[0];
    const chartLabels = chartData.labels;
    const legends: Chart.ChartLegendLabelItem[] = [];

    // 判定のインデントをそろえるために、ラベル内の最大文字数を取得する
    const maxLength: number = this.getLabelMaxLength(chartLabels);

    for (let i = 0; i < chartLabels.length; i++) {
      const lebel = chartLabels[i];
      const useCount = chartDataset.data[i];
      const backgroundColor = chartDataset.backgroundColor[i];

      const legend: Chart.ChartLegendLabelItem = {
        // 表示されるラベルはインデントを揃える
        text: this.createLegendText(lebel, useCount, maxLength),
        // 凡例ボックスの塗りつぶしスタイル
        fillStyle: backgroundColor,
        //  trueの場合、この項目は非表示のデータセットを表します。ラベルは取り消し線を伴ってレンダリングされます
        hidden: false,
        // ボックス枠用。次をご覧ください。https://developer.mozilla.org/en/docs/Web/API/CanvasRenderingContext2D/lineCap
        lineCap: "square",
        // ボックス枠用。次をご覧ください。https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/setLineDash
        lineDash: [0],
        // ボックス枠用。次をご覧ください。https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/lineDashOffset
        lineDashOffset: 0,
        // ボックス枠用。次をご覧ください。https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/lineJoin
        lineJoin: "bevel",
        // 枠線の幅
        lineWidth: 0,
        // 凡例ボックスのストロークスタイル
        strokeStyle: "",
        // 凡例ボックスのポイントスタイル（usePointStyleがtrueの場合にのみ使用されます）
        pointStyle: undefined,
        // インデックス
        datasetIndex: i
      };

      legends.push(legend);
    }

    return legends;
  }

  /**
   * ラベルの中で文字数が最長のラベルのlengthを返す
   */
  public getLabelMaxLength(labels: Array<string>): number {
    let maxLength: number = labels[0] ? labels[0].length : 0;

    for (let i = 0; i < labels.length; i++) {
      if (maxLength < labels[i].length) {
        maxLength = labels[i].length;
      }
    }

    return maxLength;
  }

  /**
   * ラベルテキストを作成する
   * ラベルのインデントは揃える必要がある
   * 最長ラベルの場合は末尾に「・」を2つ、それ以外は最長ラベルと同じ長さになるように「・」を付ける
   */
  public createLegendText(label: string, useCount: number, maxLength: number): string {
    let text: string = label;
    for (let j = 0; j < maxLength + 2 - label.length; j++) {
      text = `${text}・`;
    }

    return `${text}${useCount}${this.labelSuffix}`;
  }

  /**
   * ツールチップのラベルを作成する
   */
  public createTooltipLabel(tooltipItem: Chart.ChartTooltipItem, data: any): string {
    let label: string = "";

    if (tooltipItem.index != undefined) {
      const index: number = tooltipItem.index;
      label = data.labels[index];
      const dataCount = data.datasets[0].data[index];

      if (label) {
        label = `${label}：${dataCount}${this.labelSuffix}`;
      }
    }

    return label;
  }

  /**
   * グラフ中央に合計を表示する自作プラグイン
   */
  public static centerLabelPlugin(chart: any, options: Chart.ChartOptions): void {
    // 割合数を合計したものが表示する内容になる
    const text: string = this.createCenterText(chart);

    // 画面サイズに応じてグラフのサイズが可変するので、文字サイズは計算で求める
    const fontSize: number = this.calculateCenterFontSize(42, text, chart);

    // 描画位置はグラフの中央なので計算で求める
    const centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
    const centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

    // コンテキスト領域に文字を描画する
    const ctx = chart.chart.ctx;
    ctx.restore();
    ctx.font = fontSize + "px sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText(text, centerX, centerY);
    ctx.save();
  }

  /**
   * グラフ中央に表示するテキストを作成する
   */
  public static createCenterText(chart: any): string {
    const data: Array<number> = chart.config.data.datasets[0].data;
    let sumUseCount: number = 0;
    for (let useCount of data) {
      sumUseCount += useCount;
    }
    const labelSuffix: string = chart.config.data.labelSuffix ? chart.config.data.labelSuffix : "回";
    return `${sumUseCount}${labelSuffix}`;
  }

  /**
   * グラフサイズに応じて、フォントサイズを求める
   */
  public static calculateCenterFontSize(fontSize: number, text: string, chart: any) {
    let caluclatedFontSize: number = 0;

    let textAreaSize: { width: number; height: number } = {
      width: text.length * fontSize,
      height: fontSize
    };

    // テキスト領域をドーナツの内側の円に合わせるための調整率を計算する
    var hypotenuse = Math.sqrt(Math.pow(textAreaSize.width, 2) + Math.pow(textAreaSize.height, 2));
    var innerDiameter = chart.innerRadius * 2;
    var fitRatio = innerDiameter / hypotenuse;

    // 必要に応じてフォントを調整し、適合率を適用した後にテキスト領域を再計算します
    if (fitRatio < 1) {
      caluclatedFontSize = Math.floor(fontSize * fitRatio);
    } else {
      caluclatedFontSize = fontSize;
    }

    return caluclatedFontSize;
  }
}
</script>
